// XP and Leveling Configuration
// Adjust these values to tune the progression curve

/** XP awarded per habit completion (once per habit per day) */
export const XP_PER_HABIT = 10;

/** Mood-based XP multipliers for habits (that day only) */
const MOOD_XP_MULTIPLIERS = {
  sad: 1.5,
  happy: 1.75,
  ecstatic: 2,
};

/**
 * Get XP for a habit completion, applying mood multiplier for that day.
 * @param {number} baseXP - Base XP (e.g. XP_PER_HABIT)
 * @param {string|undefined} mood - Normalized mood id (sad, happy, ecstatic, etc.)
 * @returns {number} - XP to award
 */
export function getHabitXPWithMood(baseXP, mood) {
  if (!mood || !MOOD_XP_MULTIPLIERS[mood]) return baseXP;
  return Math.max(1, Math.round(baseXP * MOOD_XP_MULTIPLIERS[mood]));
}

/**
 * XP required to complete the current level and advance to the next.
 * Level 1 -> 2: ~50 XP (~5 habits), then increases non-linearly.
 * Formula: base * (level ^ exponent) - fast early levels, slower later.
 */
export const XP_CURVE = {
  /** Base XP for level 1 -> 2 */
  base: 20,
  /** Exponent for non-linear growth (higher = steeper curve) */
  exponent: 1.4,
};

/**
 * Calculate XP required to advance from level L to L+1
 */
export function getXPRequiredForLevel(level) {
  if (level < 1) return 0;
  return Math.floor(XP_CURVE.base * Math.pow(level, XP_CURVE.exponent));
}

/**
 * Total XP needed to reach a given level (sum of all previous levels)
 */
export function getTotalXPForLevel(level) {
  if (level <= 1) return 0;
  let total = 0;
  for (let i = 1; i < level; i++) {
    total += getXPRequiredForLevel(i);
  }
  return total;
}

/**
 * Derive level and progress from total XP
 */
export function getLevelFromTotalXP(totalXP) {
  if (!totalXP || totalXP <= 0) {
    return { level: 1, xpInLevel: 0, xpForNextLevel: getXPRequiredForLevel(1) };
  }

  let level = 1;
  let remaining = totalXP;

  while (true) {
    const xpForNext = getXPRequiredForLevel(level);
    if (remaining < xpForNext) {
      return {
        level,
        xpInLevel: remaining,
        xpForNextLevel: xpForNext,
      };
    }
    remaining -= xpForNext;
    level++;
  }
}
